import { cloneElement, Children, useState, useEffect, useCallback } from 'react';
import { IconButton } from './shared/StandardButtons';

export default function Sidebar({
  isCollapsed,
  setIsCollapsed,
  children,
  width = 64, // Default width in Tailwind units (64 = 256px)
  mode = 'push', // 'push' or 'overlay'
  resizable = false, // Enable/disable resize feature
  onWidthChange = null, // Callback when width changes (receives new width in Tailwind units)
  minWidth = 48, // Minimum width in Tailwind units (192px)
  maxWidth = 160, // Maximum width in Tailwind units (640px)
  autoCollapseOnOverlay = false // Auto-collapse when entering overlay mode
}) {
  const [currentWidth, setCurrentWidth] = useState(width);
  const [isResizing, setIsResizing] = useState(false);
  const [previousMode, setPreviousMode] = useState(mode);

  // Convert Tailwind units to pixels for button positioning
  const widthInPx = currentWidth * 4; // Tailwind: 1 unit = 4px
  const buttonLeftPosition = widthInPx - 24; // 24px offset for button
  const collapsedButtonPosition = -24; // Button position when collapsed (off-screen)

  // Calculate sidebar position based on collapse state
  const sidebarLeft = isCollapsed ? `-${widthInPx}px` : '0';
  const sidebarWidth = `${widthInPx}px`;

  // Resize handlers
  const handleMouseDown = useCallback((e) => {
    if (!resizable || isCollapsed) return;
    e.preventDefault();
    setIsResizing(true);
  }, [resizable, isCollapsed]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;

    const newWidthPx = e.clientX;
    const newWidthTailwind = Math.round(newWidthPx / 4);

    // Apply min/max constraints
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidthTailwind));

    setCurrentWidth(constrainedWidth);

    // Notify parent of width change
    if (onWidthChange) {
      onWidthChange(constrainedWidth);
    }
  }, [isResizing, minWidth, maxWidth, onWidthChange]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Auto-collapse when transitioning from push to overlay mode
  useEffect(() => {
    if (autoCollapseOnOverlay && previousMode !== mode) {
      // Transitioning from push to overlay - auto-collapse
      if (mode === 'overlay') {
        setIsCollapsed(true);
      }
      setPreviousMode(mode);
    }
  }, [mode, autoCollapseOnOverlay, previousMode, setIsCollapsed]);

  // Clone children and pass isCollapsed as a prop
  const childrenWithProps = Children.map(children, child => {
    if (child) {
      return cloneElement(child, { isCollapsed });
    }
    return child;
  });

  return (
    <>
      {/* Center Collapse/Expand Button - Outside Sidebar */}
      {!isResizing && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            left: isCollapsed ? `${collapsedButtonPosition}px` : `${buttonLeftPosition}px`
          }}
          className={`fixed top-1/2 transform -translate-y-1/2 w-12 h-16 rounded-full bg-gray-800 border-2 border-gray-700 hover:border-purple-500 hover:bg-gray-700 transition-all duration-300 flex items-center justify-center ${mode === 'overlay' ? 'z-30' : 'z-30'}`}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            className={`w-5 h-5 text-gray-400 hover:text-purple-400 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''} ml-4`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Bottom Left Expand Button - Only shown when collapsed */}
      {isCollapsed && !isResizing && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="fixed bottom-8 left-8 w-20 h-20 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center hover:scale-105 active:scale-95 opacity-90 hover:opacity-100 z-40"
          style={{ color: 'var(--color-text-on-accent)' }}
          title="Expand sidebar"
        >
          <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <aside
        className={`fixed top-0 bottom-0 bg-gray-900 border-r border-gray-700 ${isResizing ? '' : 'transition-all duration-300'} z-40`}
        style={{
          width: sidebarWidth,
          left: sidebarLeft
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Collapse Button - Inside Sidebar */}
        {!isResizing && (
          <div className="absolute top-4 right-3 z-10">
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </IconButton>
          </div>
        )}

        {childrenWithProps}

        {/* Bottom Collapse Button - Inside Sidebar */}
        {!isResizing && (
          <div className="absolute bottom-4 right-3 z-10">
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </IconButton>
          </div>
        )}

        {/* Resize Handle */}
        {resizable && !isCollapsed && (
          <div
            onMouseDown={handleMouseDown}
            className="absolute top-0 bottom-0 right-0 w-1 cursor-ew-resize hover:bg-purple-500 transition-colors duration-200 group"
            style={{ color: 'var(--color-text-on-accent)' }}
            style={{ zIndex: 50 }}
          >
            {/* Visual indicator on hover */}
            <div className="absolute inset-y-0 right-0 w-1 bg-purple-500/0 group-hover:bg-purple-500/50 transition-all duration-200"></div>
          </div>
        )}
      </aside>
    </>
  );
}
